import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { useLanguages } from "@/hooks/useLanguages";
import type { Language } from "@/hooks/useLanguages";
import manifestJson from "@/locales/manifest.json";

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Flatten a nested JSON object into dotted-key pairs: { "auth.tagline": "..." } */
function flattenJson(obj: unknown, prefix = ""): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v !== null && typeof v === "object" && !Array.isArray(v)) {
      Object.assign(out, flattenJson(v, key));
    } else {
      out[key] = String(v ?? "");
    }
  }
  return out;
}

// Eagerly import every src/locales/*.json — captures static translations baked
// into the build. DB `translation_overrides` layer on top at export time.
const localeModules = import.meta.glob<Record<string, unknown>>(
  "../../locales/*.json",
  { eager: true, import: "default" },
);

const flatBase: Record<string, Record<string, string>> = {};
for (const [path, mod] of Object.entries(localeModules)) {
  const file = path.split("/").pop() ?? "";
  // Match xx.json or xx-YY.json; skip manifest, _registry, en.meta, etc.
  const match = /^([a-z]{2}(?:-[A-Z]{2})?)\.json$/.exec(file);
  if (!match) continue;
  flatBase[match[1]] = flattenJson(mod);
}

const flatEn = flatBase.en ?? {};

// ── Types ─────────────────────────────────────────────────────────────────────

interface ManifestLocale {
  coverage_pct: number;
  total: number;
  translated: number;
  missing: number;
  stale: number;
  human_reviewed_stale: number;
  broken_interpolation: number;
  maxlen_violations: number;
  orphaned: number;
  last_translated_at: string | null;
}

interface Override {
  key: string;
  language_code: string;
  value: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: Language["status"] }) {
  const cls: Record<Language["status"], string> = {
    active:   "bg-green-100 text-green-700",
    draft:    "bg-yellow-100 text-yellow-700",
    archived: "bg-gray-100 text-gray-500",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls[status]}`}>
      {status}
    </span>
  );
}

function rowsToCsv(rows: string[][]): string {
  return rows
    .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
    .join("\n");
}

function downloadCsv(csv: string, filename: string) {
  // Prepend UTF-8 BOM so Excel opens non-ASCII characters (č, ä, ß, é…) correctly
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function exportCsv(
  langCode: string,
  overrideMap: Record<string, string>,
  baseline: Record<string, string>,
) {
  const keys = Object.keys(flatEn);
  const rows = [
    ["key", "en_value", "value", "reviewed_by", "reviewed_at"],
    ...keys.map((k) => [
      k,
      flatEn[k] ?? "",
      overrideMap[k] ?? baseline[k] ?? "",
      "",
      "",
    ]),
  ];
  downloadCsv(
    rowsToCsv(rows),
    `translations_${langCode}_${new Date().toISOString().slice(0, 10)}.csv`,
  );
}

function exportAllCsv(
  langCodes: string[],
  byKey: Record<string, Record<string, string>>,
  baselines: Record<string, Record<string, string>>,
) {
  const keys = Object.keys(flatEn);
  const rows = [
    ["key", "en", ...langCodes],
    ...keys.map((k) => [
      k,
      flatEn[k] ?? "",
      ...langCodes.map((lc) => byKey[k]?.[lc] ?? baselines[lc]?.[k] ?? ""),
    ]),
  ];
  downloadCsv(
    rowsToCsv(rows),
    `translations_all_${new Date().toISOString().slice(0, 10)}.csv`,
  );
}

function parseCsv(text: string): Array<Record<string, string>> {
  const lines = text.trim().split("\n");
  if (lines.length < 2) return [];

  const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));

  return lines.slice(1).map((line) => {
    const values: string[] = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
        else inQuotes = !inQuotes;
      } else if (ch === "," && !inQuotes) {
        values.push(current); current = "";
      } else {
        current += ch;
      }
    }
    values.push(current);
    return Object.fromEntries(headers.map((h, i) => [h, values[i] ?? ""]));
  });
}

// ── Manifest health panel ────────────────────────────────────────────────────

const manifestData = manifestJson as {
  _generated?: string;
  _runner: string;
  total_keys: number;
  locales: Record<string, ManifestLocale>;
} | null;

function ManifestHealth() {
  if (!manifestData) return null;

  const generated = new Date(manifestData._generated);
  const ageMs = Date.now() - generated.getTime();
  const ageHours = Math.round(ageMs / 3_600_000);
  const ageLabel = ageHours < 1 ? "< 1h ago" : ageHours < 24 ? `${ageHours}h ago` : `${Math.round(ageHours / 24)}d ago`;

  const localeEntries = Object.entries(manifestData.locales);

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <h2 className="text-sm font-semibold text-gray-900">Pipeline Health</h2>
        <span className="text-xs text-gray-400">
          Last audit: <span className="text-gray-600">{ageLabel}</span>
          {" · "}
          <code className="text-[11px] bg-gray-100 rounded px-1">{manifestData._runner}</code>
        </span>
      </div>

      {localeEntries.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-gray-400">
          No locales found in manifest. Run{" "}
          <code className="bg-gray-100 rounded px-1 text-[11px]">npm run i18n:audit</code>.
        </div>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th className="text-left px-5 py-2.5 font-medium text-gray-500">Locale</th>
              <th className="text-right px-3 py-2.5 font-medium text-gray-500">Coverage</th>
              <th className="text-right px-3 py-2.5 font-medium text-gray-500">Missing</th>
              <th className="text-right px-3 py-2.5 font-medium text-gray-500">Stale</th>
              <th className="text-right px-3 py-2.5 font-medium text-gray-500">Interp.</th>
              <th className="text-right px-3 py-2.5 font-medium text-gray-500">Maxlen</th>
              <th className="text-right px-3 py-2.5 font-medium text-gray-500">Orphaned</th>
              <th className="text-right px-5 py-2.5 font-medium text-gray-500">Last run</th>
            </tr>
          </thead>
          <tbody>
            {localeEntries.map(([code, loc]) => {
              const hasIssues =
                loc.missing > 0 || loc.stale > 0 ||
                loc.broken_interpolation > 0 || loc.maxlen_violations > 0;
              const coverageColor =
                loc.coverage_pct === 100 ? "text-green-600" :
                loc.coverage_pct > 80 ? "text-yellow-600" : "text-red-500";
              const lastRun = loc.last_translated_at
                ? new Date(loc.last_translated_at).toLocaleDateString()
                : "—";

              return (
                <tr
                  key={code}
                  className="border-b border-gray-100 last:border-0 hover:bg-gray-50/30 transition-colors"
                >
                  <td className="px-5 py-2.5 font-mono font-medium text-gray-900">{code}</td>
                  <td className={`px-3 py-2.5 text-right font-semibold ${coverageColor}`}>
                    {loc.coverage_pct}%
                  </td>
                  <td className={`px-3 py-2.5 text-right ${loc.missing > 0 ? "text-red-500 font-medium" : "text-gray-400"}`}>
                    {loc.missing || "—"}
                  </td>
                  <td className={`px-3 py-2.5 text-right ${loc.stale > 0 ? "text-yellow-600 font-medium" : "text-gray-400"}`}>
                    {loc.stale || "—"}
                  </td>
                  <td className={`px-3 py-2.5 text-right ${loc.broken_interpolation > 0 ? "text-orange-500 font-medium" : "text-gray-400"}`}>
                    {loc.broken_interpolation || "—"}
                  </td>
                  <td className={`px-3 py-2.5 text-right ${loc.maxlen_violations > 0 ? "text-orange-500 font-medium" : "text-gray-400"}`}>
                    {loc.maxlen_violations || "—"}
                  </td>
                  <td className={`px-3 py-2.5 text-right ${loc.orphaned > 0 ? "text-yellow-600 font-medium" : "text-gray-400"}`}>
                    {loc.orphaned || "—"}
                  </td>
                  <td className="px-5 py-2.5 text-right text-gray-400">{lastRun}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="px-5 py-2.5 border-t border-gray-100 bg-gray-50/40">
        <p className="text-[11px] text-gray-400">
          Read-only. To translate:{" "}
          <code className="bg-gray-100 rounded px-1">npm run i18n:translate sk</code>
          {" · "}
          To audit:{" "}
          <code className="bg-gray-100 rounded px-1">npm run i18n:audit</code>
        </p>
      </div>
    </div>
  );
}

// ── Tab types ─────────────────────────────────────────────────────────────────

type StatusTab = "all" | Language["status"];

const STATUS_TABS: Array<{ key: StatusTab; label: string }> = [
  { key: "all",      label: "All" },
  { key: "active",   label: "Active" },
  { key: "draft",    label: "Draft" },
  { key: "archived", label: "Archived" },
];

// ── Main component ─────────────────────────────────────────────────────────────

export default function TranslationManager() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { all: allLanguages, isLoading: langsLoading } = useLanguages();

  // Language table state
  const [statusTab, setStatusTab] = useState<StatusTab>("all");
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({
    code: "", name: "", flag_emoji: "", status: "draft" as Language["status"],
  });
  const [savingAdd, setSavingAdd] = useState(false);

  // Translation keys state
  const nonDefaultLanguages = allLanguages.filter((l) => !l.is_default && l.status !== "archived");
  const [selectedLang, setSelectedLang] = useState<string>(() =>
    nonDefaultLanguages[0]?.code ?? "sk"
  );
  const [importing, setImporting] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);
  const importRef = useRef<HTMLInputElement>(null);

  // Per-language overrides for the admin table (includes metadata)
  const { data: overrides = [] } = useQuery<Override[]>({
    queryKey: ["translation-overrides-admin", selectedLang],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("translation_overrides")
        .select("key, language_code, value, reviewed_by, reviewed_at")
        .eq("language_code", selectedLang);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!selectedLang,
    staleTime: 60_000,
  });

  const overrideMap = Object.fromEntries(overrides.map((o) => [o.key, o.value]));
  const enKeys = Object.keys(flatEn);

  // Filtered languages for the table
  const visibleLanguages =
    statusTab === "all"
      ? allLanguages
      : allLanguages.filter((l) => l.status === statusTab);

  // ── Language CRUD ────────────────────────────────────────────────────────────

  const handleAddLanguage = async () => {
    if (!addForm.code.trim() || !addForm.name.trim()) return;
    setSavingAdd(true);
    const { error } = await supabase.from("languages").insert({
      code:       addForm.code.toLowerCase().trim(),
      name:       addForm.name.trim(),
      flag_emoji: addForm.flag_emoji.trim(),
      status:     addForm.status,
      is_default: false,
      sort_order: allLanguages.length,
    });
    setSavingAdd(false);
    if (error) { toast.error(`Could not add: ${error.message}`); return; }
    toast.success(`${addForm.name} added`);
    setAddForm({ code: "", name: "", flag_emoji: "", status: "draft" });
    setShowAddForm(false);
    queryClient.invalidateQueries({ queryKey: ["languages"] });
  };

  const handleStatusChange = async (lang: Language, newStatus: Language["status"]) => {
    const { error } = await supabase
      .from("languages")
      .update({ status: newStatus })
      .eq("code", lang.code);
    if (error) { toast.error(`Could not update: ${error.message}`); return; }
    toast.success(`${lang.name} is now ${newStatus}`);
    queryClient.invalidateQueries({ queryKey: ["languages"] });
  };

  const handleDelete = async (lang: Language) => {
    if (!confirm(`Delete ${lang.name}? All translation overrides for this language will also be removed.`)) return;
    const { error } = await supabase.from("languages").delete().eq("code", lang.code);
    if (error) { toast.error(`Could not delete: ${error.message}`); return; }
    toast.success(`${lang.name} deleted`);
    queryClient.invalidateQueries({ queryKey: ["languages"] });
  };

  // ── CSV ──────────────────────────────────────────────────────────────────────

  const handleExport = () =>
    exportCsv(selectedLang, overrideMap, flatBase[selectedLang] ?? {});

  const handleExportAll = async () => {
    const langCodes = nonDefaultLanguages.map((l) => l.code);
    if (langCodes.length === 0) {
      toast.error("No target languages to export");
      return;
    }
    setExportingAll(true);
    try {
      const { data, error } = await supabase
        .from("translation_overrides")
        .select("key, language_code, value")
        .in("language_code", langCodes);
      if (error) {
        toast.error(`Export failed: ${error.message}`);
        return;
      }
      const byKey: Record<string, Record<string, string>> = {};
      for (const row of data ?? []) {
        if (!byKey[row.key]) byKey[row.key] = {};
        byKey[row.key][row.language_code] = row.value;
      }
      exportAllCsv(langCodes, byKey, flatBase);
      toast.success(`Exported ${langCodes.length} languages`);
    } finally {
      setExportingAll(false);
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const rows = parseCsv(text);

      const toUpsert = rows
        .filter((row) => row.key?.trim() && row.value?.trim())
        .map((row) => ({
          key:           row.key.trim(),
          language_code: selectedLang,
          value:         row.value.trim(),
          reviewed_by:   row.reviewed_by?.trim() || user?.email || null,
          reviewed_at:   row.reviewed_at?.trim() || new Date().toISOString(),
        }));

      if (toUpsert.length === 0) { toast.error("No valid rows found in the CSV"); return; }

      const { error } = await supabase
        .from("translation_overrides")
        .upsert(toUpsert, { onConflict: "key,language_code" });

      if (error) { toast.error(`Import failed: ${error.message}`); return; }

      toast.success(`Imported ${toUpsert.length} translation${toUpsert.length !== 1 ? "s" : ""}`);
      queryClient.invalidateQueries({ queryKey: ["translation-overrides-admin", selectedLang] });
      queryClient.invalidateQueries({ queryKey: ["translation-overrides"] });
    } catch {
      toast.error("Could not parse CSV — check the file format");
    } finally {
      setImporting(false);
      if (importRef.current) importRef.current.value = "";
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="p-8 max-w-5xl space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-gray-900">Translations</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage languages and override translation keys. Changes take effect immediately for all users.
        </p>
      </div>

      {/* ── Pipeline health ─────────────────────────────────────────────── */}
      <ManifestHealth />

      {/* ── Language management ─────────────────────────────────────────── */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">Languages</h2>
          <button
            onClick={() => setShowAddForm((v) => !v)}
            className="text-xs font-medium text-orange-600 hover:text-orange-700 transition-colors"
          >
            {showAddForm ? "Cancel" : "+ Add language"}
          </button>
        </div>

        {/* Status tabs */}
        <div className="flex border-b border-gray-100 px-5">
          {STATUS_TABS.map((tab) => {
            const count = tab.key !== "all"
              ? allLanguages.filter((l) => l.status === tab.key).length
              : null;
            return (
              <button
                key={tab.key}
                onClick={() => setStatusTab(tab.key)}
                className={`py-2.5 px-3 text-xs font-medium border-b-2 transition-colors mr-1 ${
                  statusTab === tab.key
                    ? "border-orange-500 text-orange-700"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
                {count !== null && (
                  <span className="ml-1.5 text-[10px] text-gray-400">({count})</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Inline add form */}
        {showAddForm && (
          <div className="px-5 py-4 bg-orange-50/40 border-b border-orange-100 flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">Code</label>
              <input
                value={addForm.code}
                onChange={(e) => setAddForm((f) => ({ ...f, code: e.target.value }))}
                placeholder="cs"
                maxLength={10}
                className="h-8 w-20 rounded-md border border-gray-200 px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">Name</label>
              <input
                value={addForm.name}
                onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Czech"
                className="h-8 w-36 rounded-md border border-gray-200 px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">Flag</label>
              <input
                value={addForm.flag_emoji}
                onChange={(e) => setAddForm((f) => ({ ...f, flag_emoji: e.target.value }))}
                placeholder="🇨🇿"
                className="h-8 w-16 rounded-md border border-gray-200 px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">Status</label>
              <select
                value={addForm.status}
                onChange={(e) => setAddForm((f) => ({ ...f, status: e.target.value as Language["status"] }))}
                className="h-8 rounded-md border border-gray-200 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
              </select>
            </div>
            <button
              onClick={handleAddLanguage}
              disabled={savingAdd || !addForm.code.trim() || !addForm.name.trim()}
              className="h-8 px-4 rounded-md bg-orange-600 text-white text-xs font-medium hover:bg-orange-700 disabled:opacity-50 transition-colors"
            >
              {savingAdd ? "Adding…" : "Add"}
            </button>
          </div>
        )}

        {/* Table */}
        {langsLoading ? (
          <div className="px-5 py-10 text-center text-sm text-gray-400">Loading…</div>
        ) : visibleLanguages.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-gray-400">
            No languages with status "{statusTab}"
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-5 py-2.5 text-xs font-medium text-gray-500 w-12">Flag</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Language</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 w-20">Code</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 w-24">Status</th>
                <th className="px-5 py-2.5 text-xs font-medium text-gray-500 w-40 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleLanguages.map((lang) => (
                <tr
                  key={lang.code}
                  className="border-b border-gray-100 last:border-0 hover:bg-gray-50/40 transition-colors"
                >
                  <td className="px-5 py-3 text-xl">{lang.flag_emoji}</td>
                  <td className="px-3 py-3">
                    <span className="font-medium text-gray-900">{lang.name}</span>
                    {lang.is_default && (
                      <span className="ml-2 text-[10px] bg-gray-100 text-gray-400 rounded-full px-2 py-0.5">
                        default
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 font-mono text-xs text-gray-500">{lang.code}</td>
                  <td className="px-3 py-3"><StatusBadge status={lang.status} /></td>
                  <td className="px-5 py-3 text-right">
                    {!lang.is_default && (
                      <div className="flex items-center justify-end gap-3">
                        {lang.status !== "active" && (
                          <button
                            onClick={() => handleStatusChange(lang, "active")}
                            className="text-xs font-medium text-green-600 hover:text-green-700"
                          >
                            Activate
                          </button>
                        )}
                        {lang.status === "active" && (
                          <button
                            onClick={() => handleStatusChange(lang, "archived")}
                            className="text-xs font-medium text-gray-500 hover:text-gray-700"
                          >
                            Archive
                          </button>
                        )}
                        {lang.status === "draft" && (
                          <button
                            onClick={() => handleStatusChange(lang, "archived")}
                            className="text-xs font-medium text-gray-500 hover:text-gray-700"
                          >
                            Archive
                          </button>
                        )}
                        {lang.status !== "active" && (
                          <button
                            onClick={() => handleDelete(lang)}
                            className="text-xs font-medium text-red-500 hover:text-red-600"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Translation keys ────────────────────────────────────────────── */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-semibold text-gray-900">Translation Keys</h2>
            {nonDefaultLanguages.length > 0 && (
              <select
                value={selectedLang}
                onChange={(e) => setSelectedLang(e.target.value)}
                className="h-7 rounded-md border border-gray-200 px-2 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-400"
              >
                {nonDefaultLanguages.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.flag_emoji} {l.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              disabled={enKeys.length === 0}
              title={enKeys.length === 0 ? "en.json has no keys yet" : undefined}
              className="h-7 px-3 rounded-md border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Export CSV
            </button>
            <button
              onClick={handleExportAll}
              disabled={enKeys.length === 0 || exportingAll || nonDefaultLanguages.length === 0}
              title={
                enKeys.length === 0
                  ? "en.json has no keys yet"
                  : nonDefaultLanguages.length === 0
                  ? "No target languages"
                  : "All languages in one CSV — read-only, for side-by-side review"
              }
              className="h-7 px-3 rounded-md border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {exportingAll ? "Exporting…" : "Export all languages"}
            </button>
            <button
              onClick={() => importRef.current?.click()}
              disabled={importing || nonDefaultLanguages.length === 0}
              className="h-7 px-3 rounded-md bg-orange-600 text-white text-xs font-medium hover:bg-orange-700 disabled:opacity-50 transition-colors"
            >
              {importing ? "Importing…" : "Import CSV"}
            </button>
            <input
              ref={importRef}
              type="file"
              accept=".csv,text/csv"
              className="sr-only"
              onChange={handleImport}
            />
          </div>
        </div>

        {/* CSV format hint */}
        <div className="px-5 py-2.5 border-b border-gray-100 bg-gray-50/40">
          <p className="text-xs text-gray-400">
            CSV columns:{" "}
            <code className="text-[11px] bg-gray-100 rounded px-1">key</code>{" "}
            <code className="text-[11px] bg-gray-100 rounded px-1">en_value</code>{" "}
            <code className="text-[11px] bg-gray-100 rounded px-1">value</code>{" "}
            <code className="text-[11px] bg-gray-100 rounded px-1">reviewed_by</code>{" "}
            <code className="text-[11px] bg-gray-100 rounded px-1">reviewed_at</code>
            {" "}— only <code className="text-[11px] bg-gray-100 rounded px-1">key</code> and{" "}
            <code className="text-[11px] bg-gray-100 rounded px-1">value</code> are required on import.
          </p>
        </div>

        {/* Key table */}
        {enKeys.length === 0 ? (
          <div className="px-5 py-14 text-center">
            <p className="text-sm font-medium text-gray-500">No translation keys yet</p>
            <p className="text-xs text-gray-400 mt-1.5 max-w-sm mx-auto leading-relaxed">
              Keys appear once{" "}
              <code className="bg-gray-100 rounded px-1 text-[11px]">en.json</code> is populated.
              Run <code className="bg-gray-100 rounded px-1 text-[11px]">npm run i18n:audit</code> to
              find hardcoded strings, then wire them with{" "}
              <code className="bg-gray-100 rounded px-1 text-[11px]">t()</code>.
            </p>
            {nonDefaultLanguages.length > 0 && (
              <p className="text-xs text-gray-400 mt-3">
                You can still import a CSV to pre-populate{" "}
                <strong>{allLanguages.find((l) => l.code === selectedLang)?.name ?? selectedLang}</strong>{" "}
                translations before keys are wired.
              </p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left px-5 py-2.5 text-xs font-medium text-gray-500 w-48">Key</th>
                  <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">English</th>
                  <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Translation</th>
                  <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 w-32">Reviewed by</th>
                  <th className="text-left px-5 py-2.5 text-xs font-medium text-gray-500 w-24">Updated</th>
                </tr>
              </thead>
              <tbody>
                {enKeys.map((key) => {
                  const override = overrides.find((o) => o.key === key);
                  return (
                    <tr
                      key={key}
                      className="border-b border-gray-100 last:border-0 hover:bg-gray-50/30 transition-colors"
                    >
                      <td className="px-5 py-3 font-mono text-[11px] text-gray-400 max-w-[192px] truncate">
                        {key}
                      </td>
                      <td className="px-3 py-3 text-gray-700 max-w-[220px]">
                        <span className="line-clamp-2 text-xs">{flatEn[key]}</span>
                      </td>
                      <td className="px-3 py-3 max-w-[220px]">
                        {override ? (
                          <span className="text-gray-900 line-clamp-2 text-xs">{override.value}</span>
                        ) : (
                          <span className="text-gray-300 text-[11px] italic">— not translated</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-400 truncate">
                        {override?.reviewed_by ?? "—"}
                      </td>
                      <td className="px-5 py-3 text-xs text-gray-400">
                        {override?.reviewed_at
                          ? new Date(override.reviewed_at).toLocaleDateString()
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
